import { useState, useEffect, useRef } from "react";
import { ImageOverlay, useMapEvents } from "react-leaflet";
import "./styles.css";
import { Z } from "../../global/constants/zIndex";
import { geoServerAuthHeader } from "../../services/geoServerAuth";

interface ElevationWMSLayerProps {
  typename: string;
  workspace: string;
  zIndex?: number;
  elevationValue: number;
  /**
   * WMS parameter carrying the dimension value. Defaults to `elevation`; custom
   * ImageMosaic dimensions use `DIM_<NAME>` (e.g. `DIM_DIRECTION`, `DIM_YEAR`).
   */
  dimensionParam?: string;
  opacity?: number;
}

const GEOSERVER_URL = "https://geoserver-atlas.isitics.com/geoserver";

interface ImageState {
  url: string;
  bounds: [[number, number], [number, number]];
  key: string;
  /** Definido quando `url` é um object URL e precisa ser liberado depois. */
  objectUrl?: string;
}

/**
 * Optimized WMS raster layer with elevation dimension and true crossfade.
 */
export function ElevationWMSLayer({
  typename,
  workspace,
  zIndex = Z.MAP_LAYER_DEFAULT,
  elevationValue,
  dimensionParam = "elevation",
  opacity = 0.8,
}: ElevationWMSLayerProps) {
  const [oldImage, setOldImage] = useState<ImageState | null>(null);
  const [currentImage, setCurrentImage] = useState<ImageState | null>(null);

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const requestIdRef = useRef(0);
  const preloadImageRef = useRef<HTMLImageElement | null>(null);
  const cleanupTimerRef = useRef<NodeJS.Timeout | null>(null);
  const fetchAbortControllerRef = useRef<AbortController | null>(null);
  // Object URL do preload em andamento que ainda não disparou onload/onerror.
  // Cancelar o preload (nova chamada de loadImage, ou unmount) limpa o <img>
  // mas não dispara esses handlers, então é este ref que garante o revoke.
  const pendingObjectUrlRef = useRef<string | null>(null);

  // Use refs to avoid circular dependencies
  const currentImageRef = useRef<ImageState | null>(null);
  currentImageRef.current = currentImage;
  const oldImageRef = useRef<ImageState | null>(null);
  oldImageRef.current = oldImage;

  const map = useMapEvents({
    moveend: () => {
      scheduleUpdate();
    },
    zoomend: () => {
      scheduleUpdate();
    },
  });

  // Build WMS URL - no dependencies that change
  const buildImageUrl = () => {
    const mapBounds = map.getBounds();
    const size = map?.getSize();
    const bbox = `${mapBounds.getWest()},${mapBounds.getSouth()},${mapBounds.getEast()},${mapBounds.getNorth()}`;

    return (
      `${GEOSERVER_URL}/${workspace}/wms?` +
      `service=WMS&version=1.1.0&request=GetMap&` +
      `layers=${workspace}:${typename}&` +
      `bbox=${bbox}&width=${size.x}&height=${size.y}&` +
      `srs=EPSG:4326&format=image/png&transparent=true&` +
      `${dimensionParam}=${elevationValue}`
    );
  };

  // Preload and swap images
  const loadImage = (url: string, bounds: [[number, number], [number, number]], requestId: number) => {
    // Cancel existing preload
    if (preloadImageRef.current) {
      preloadImageRef.current.onload = null;
      preloadImageRef.current.onerror = null;
      preloadImageRef.current.src = "";
    }
    // O preload acima nunca vai disparar onload/onerror agora, então o blob
    // que ele estava carregando (se já tinha chegado do fetch autenticado)
    // fica órfão — revoga aqui para não vazar.
    if (pendingObjectUrlRef.current) {
      URL.revokeObjectURL(pendingObjectUrlRef.current);
      pendingObjectUrlRef.current = null;
    }

    const img = new Image();
    img.crossOrigin = "anonymous";
    preloadImageRef.current = img;

    const startPreload = (displayUrl: string, objectUrl?: string) => {
      img.onload = () => {
        // A partir daqui o blob passa a ser responsabilidade do ImageState
        // (revogado via cleanupTimerRef/unmount) ou é revogado abaixo — o
        // preload deixou de estar pendente.
        if (objectUrl && pendingObjectUrlRef.current === objectUrl) {
          pendingObjectUrlRef.current = null;
        }

        if (requestId !== requestIdRef.current) {
          // Uma requisição mais nova venceu a corrida: descarta este blob.
          if (objectUrl) URL.revokeObjectURL(objectUrl);
          return;
        }

        const newImageState: ImageState = {
          url: displayUrl,
          bounds,
          key: `${Date.now()}`,
          objectUrl,
        };

        // Crossfade: current becomes old, new becomes current
        const outgoing = currentImageRef.current;

        // Se uma transição anterior ainda não terminou, seu revoke agendado
        // está prestes a ser perdido (o oldImage que ele apontava vai ser
        // sobrescrito abaixo). Revoga agora para não vazar o blob.
        if (cleanupTimerRef.current) {
          clearTimeout(cleanupTimerRef.current);
          const staleOld = oldImageRef.current;
          if (staleOld?.objectUrl) URL.revokeObjectURL(staleOld.objectUrl);
        }

        setOldImage(outgoing);
        setCurrentImage(newImageState);

        // Clean up old image after transition
        cleanupTimerRef.current = setTimeout(() => {
          setOldImage(null);
          if (outgoing?.objectUrl) URL.revokeObjectURL(outgoing.objectUrl);
        }, 250);
      };

      img.onerror = () => {
        if (objectUrl && pendingObjectUrlRef.current === objectUrl) {
          pendingObjectUrlRef.current = null;
        }
        if (objectUrl) URL.revokeObjectURL(objectUrl);
        console.error("Failed to load WMS image");
      };

      img.src = displayUrl;
    };

    const authHeader = geoServerAuthHeader(workspace);
    if (!authHeader) {
      startPreload(url);
      return;
    }

    // Workspace protegido: `<img>` não carrega cabeçalho `Authorization`, então
    // a imagem vem por `fetch` autenticado e é exibida como object URL.
    // O AbortController garante que, se o componente desmontar ou uma nova
    // requisição substituir esta, a fetch em andamento é cancelada e nunca
    // chega a criar/exibir um object URL depois do fato.
    fetchAbortControllerRef.current?.abort();
    const abortController = new AbortController();
    fetchAbortControllerRef.current = abortController;

    fetch(url, { headers: { Authorization: authHeader }, signal: abortController.signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`GeoServer respondeu ${response.status}`);
        }
        return response.blob();
      })
      .then((blob) => {
        if (requestId !== requestIdRef.current) return;
        const objectUrl = URL.createObjectURL(blob);
        pendingObjectUrlRef.current = objectUrl;
        startPreload(objectUrl, objectUrl);
      })
      .catch((error) => {
        if (error?.name === "AbortError") return;
        console.error("Failed to load WMS image", error);
      });
  };

  // Schedule update with debounce
  const scheduleUpdate = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const currentRequestId = ++requestIdRef.current;

    debounceTimerRef.current = setTimeout(() => {
      if (currentRequestId === requestIdRef.current) {
        const url = buildImageUrl();
        const mapBounds = map.getBounds();
        const bounds: [[number, number], [number, number]] = [
          [mapBounds.getSouth(), mapBounds.getWest()],
          [mapBounds.getNorth(), mapBounds.getEast()],
        ];

        loadImage(url, bounds, currentRequestId);
      }
    }, 50);
  };

  // Only trigger on dimension changes - NO FUNCTION DEPENDENCIES
  useEffect(() => {
    scheduleUpdate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elevationValue, dimensionParam]);

  // Initial load - runs ONCE
  useEffect(() => {
    scheduleUpdate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      if (cleanupTimerRef.current) clearTimeout(cleanupTimerRef.current);
      // Cancela qualquer fetch autenticado em andamento para que uma resposta
      // tardia nunca crie/exiba um object URL após o desmonte.
      fetchAbortControllerRef.current?.abort();
      if (preloadImageRef.current) {
        preloadImageRef.current.onload = null;
        preloadImageRef.current.onerror = null;
        preloadImageRef.current.src = "";
      }
      // Preload cancelado acima nunca vai disparar onload/onerror: revoga o
      // blob que ele estava carregando, se houver.
      if (pendingObjectUrlRef.current) {
        URL.revokeObjectURL(pendingObjectUrlRef.current);
        pendingObjectUrlRef.current = null;
      }
      // Blobs ainda em tela precisam ser liberados ao desmontar a camada.
      if (currentImageRef.current?.objectUrl) {
        URL.revokeObjectURL(currentImageRef.current.objectUrl);
      }
      if (oldImageRef.current?.objectUrl) {
        URL.revokeObjectURL(oldImageRef.current.objectUrl);
      }
    };
  }, []);

  return (
    <>
      {oldImage && (
        <ImageOverlay
          key={oldImage.key}
          url={oldImage.url}
          bounds={oldImage.bounds}
          opacity={0}
          zIndex={zIndex}
          pane="tilePane"
          className="seamless-overlay old-overlay"
          crossOrigin="anonymous"
        />
      )}

      {currentImage && (
        <ImageOverlay
          key={currentImage.key}
          url={currentImage.url}
          bounds={currentImage.bounds}
          opacity={opacity}
          zIndex={zIndex + 1}
          pane="tilePane"
          className="seamless-overlay current-overlay"
          crossOrigin="anonymous"
        />
      )}
    </>
  );
}
